import { Card, PrimaryBtn } from './healthShared';

const ACTIONS_BY_GOAL = {
  'Lose weight': ['Skip sugar in your chai/coffee today', 'Take the stairs for every floor today', 'Eat dinner before 8:30 pm tonight', 'Fill half your plate with vegetables at lunch', 'Walk 10 minutes after dinner', 'Say no to one fried snack today', 'Drink a glass of water before each meal'],
  'Build muscle': ['Add one palm-sized protein portion to lunch', 'Do 20 squats between patients today', 'Eat protein within an hour of waking', 'Do 2 sets of push-ups before your shower', 'Add paneer, dal or eggs to dinner', 'Carry roasted chana as your snack today', 'Hold a 30-second plank, twice today'],
  'Improve fitness': ['Take a brisk 10-minute walk at lunch', 'Use stairs instead of the lift all day', 'Do 10 squats every time you have chai', 'Park farther away and walk', 'Stretch for 2 minutes between OPD patients', 'Do one 10-minute workout today', 'Walk while taking phone calls today'],
  'Improve energy': ['Drink a full glass of water right now', 'No caffeine after 2 pm today', 'Step into sunlight for 5 minutes this morning', 'Eat a fruit instead of biscuits with chai', 'Take 5 deep breaths before your next patient', 'Have a protein-rich breakfast tomorrow', 'Take a 10-minute walk instead of a third coffee'],
  'Improve sleep': ['No screens 30 minutes before bed tonight', 'Last caffeine before 2 pm today', 'Set a fixed bedtime alarm tonight', 'Keep your bedroom cooler tonight', 'Do 2 minutes of slow breathing in bed', 'Avoid heavy dinner after 9 pm', 'Dim the lights an hour before sleep'],
  'Improve overall health': ['Drink water before every chai today', 'Take a 10-minute walk after lunch', 'Add one extra vegetable serving today', 'Do 2 minutes of deep breathing between patients', 'Sleep 30 minutes earlier tonight', 'Take the stairs all day today', 'Eat one fruit as your evening snack'],
};

const dayOfYear = () => Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);

const MAIN_CARDS = [
  { id: 'myhealth', icon: '🩺', title: 'MY HEALTH', sub: 'Profile, metrics & health checks' },
  { id: 'eat', icon: '🥗', title: 'EAT BETTER', sub: 'Practical Indian meal ideas' },
  { id: 'move', icon: '🏃', title: 'MOVE', sub: '10-minute workouts for busy doctors' },
  { id: 'sleep', icon: '😴', title: 'SLEEP & RECOVER', sub: 'Sleep, stress & quick resets' },
  { id: 'coach', icon: '🤖', title: 'AI HEALTH COACH', sub: 'Ask Gynovate Health anything' },
];

const DAY_TYPES = ['Normal day', 'OPD', 'OT', 'Night duty', 'Travel', 'Conference'];

export default function HealthHome({ profile, snapshot, go, onMyDay }) {
  const action = profile ? ACTIONS_BY_GOAL[profile.goal]?.[dayOfYear() % 7] || ACTIONS_BY_GOAL['Improve overall health'][dayOfYear() % 7] : null;

  return (
    <div data-testid="health-home" className="space-y-5">
      {/* Header */}
      <div className="pt-2">
        <p className="text-[11px] font-black tracking-[0.25em] uppercase text-[#7FA183]">Gynovate Health</p>
        <h1 className="font-accent text-3xl sm:text-4xl text-[#273127] mt-1 leading-tight">Your Health. Your Dashboard.</h1>
        <p className="text-sm text-[#66735F] mt-2 italic">“Doctors take care of everyone. Now take care of yourself.”</p>
      </div>

      {/* Snapshot or Start CTA */}
      {profile && snapshot ? (
        <div data-testid="health-snapshot-strip" className="space-y-3">
          {/* BMI hero tile */}
          <div
            className="rounded-3xl p-5 text-white shadow-[0_14px_40px_-12px_rgba(46,74,59,0.35)]"
            style={{
              background:
                snapshot.bmi_category === 'Normal' ? 'linear-gradient(135deg,#2E7D5B,#1F5F44)'
                : snapshot.bmi_category === 'Overweight' ? 'linear-gradient(135deg,#C98A2D,#A96F1F)'
                : snapshot.bmi_category === 'Obese' ? 'linear-gradient(135deg,#C25B4A,#9E4435)'
                : 'linear-gradient(135deg,#4A7FB5,#3A6697)',
            }}
            data-testid="snapshot-bmi-tile"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] font-black tracking-[0.25em] uppercase text-white/70">Body Mass Index</p>
                <p className="font-accent text-5xl mt-1 leading-none">{snapshot.bmi}</p>
              </div>
              <span className="px-3.5 py-1.5 rounded-full bg-white/20 backdrop-blur text-xs font-black tracking-wide">{snapshot.bmi_category}</span>
            </div>
            <div className="mt-5">
              <div className="relative h-2.5 rounded-full" style={{ background: 'linear-gradient(90deg,#93c5fd 0 17%,#6ee7b7 17% 40%,#fde68a 40% 50%,#fca5a5 50% 100%)' }}>
                <span
                  className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white shadow-md border-2"
                  style={{ left: `calc(${Math.min(98, Math.max(2, ((snapshot.bmi - 15) / 20) * 100))}% - 8px)`, borderColor: 'rgba(0,0,0,0.25)' }}
                />
              </div>
              <div className="flex justify-between text-[9px] font-bold text-white/60 mt-1.5"><span>15</span><span>18.5</span><span>23</span><span>25</span><span>35</span></div>
            </div>
          </div>
          {/* Stat tiles */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { i: '⚖️', l: 'Weight', v: `${profile.weight_kg} kg`, s: profile.waist_cm ? `waist ${profile.waist_cm} cm` : '', bg: '#FFF4E8', c: '#9A6B3F' },
              { i: '⚡', l: 'Activity', v: profile.activity.split(' ')[0], s: profile.activity.split(' ')[1] || '', bg: '#EDF4FB', c: '#3A6697' },
              { i: '🌙', l: 'Sleep', v: `${profile.sleep_hours} hrs`, s: `target ${snapshot.sleep_target}`, bg: '#F1EDFA', c: '#6D5BA8' },
              { i: '🏆', l: 'Goal', v: profile.goal.split(' ').slice(0, 2).join(' '), s: profile.goal.split(' ').slice(2).join(' '), bg: '#FBEFF0', c: '#A85565' },
            ].map((t) => (
              <div key={t.l} className="rounded-3xl p-4 border border-black/[0.03] shadow-sm" style={{ background: t.bg }} data-testid={`snapshot-tile-${t.l.toLowerCase()}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-base">{t.i}</span>
                  <span className="text-[9px] font-black tracking-[0.2em] uppercase" style={{ color: t.c, opacity: 0.75 }}>{t.l}</span>
                </div>
                <p className="font-accent text-xl leading-tight" style={{ color: t.c }}>{t.v}</p>
                {t.s && <p className="text-[10px] font-semibold mt-0.5" style={{ color: t.c, opacity: 0.6 }}>{t.s}</p>}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <Card className="p-6 text-center" testId="health-start-card">
          <p className="text-2xl mb-2">🌿</p>
          <p className="font-accent text-xl text-[#273127]">One minute to your Health Snapshot</p>
          <p className="text-sm text-[#66735F] mt-1 mb-4">Answer 10 quick questions. Everything gets personalised.</p>
          <PrimaryBtn onClick={() => go('assessment')} testId="start-health-plan-btn" className="w-full py-4 text-base">START MY HEALTH PLAN</PrimaryBtn>
        </Card>
      )}

      {/* TODAY card */}
      {profile && snapshot && (
        <Card className="p-5" testId="health-today-card">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] font-black tracking-[0.2em] uppercase text-[#7FA183]">Today</p>
            <p className="text-[11px] text-[#9AA594]">{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })}</p>
          </div>
          <div className="space-y-2.5 text-sm text-[#33402F]">
            <p>🏃 <strong>Move:</strong> 30 min <button onClick={() => go('move')} className="text-[#2E4A3B] underline underline-offset-2 font-semibold ml-1" data-testid="today-move-link">get a workout</button></p>
            <p>🥗 <strong>Eat:</strong> Protein + vegetables with each meal</p>
            <p>💧 <strong>Hydrate:</strong> {snapshot.hydration_l} L through the day</p>
            <p>😴 <strong>Sleep:</strong> aim {snapshot.sleep_target} tonight</p>
          </div>
          <div className="mt-4 rounded-2xl bg-[#EDF2EB] border border-[#DCE5DA] px-4 py-3" data-testid="one-health-action">
            <p className="text-[10px] font-black tracking-widest uppercase text-[#7FA183]">One Health Action</p>
            <p className="text-sm font-bold text-[#2E4A3B] mt-0.5">{action}</p>
          </div>
        </Card>
      )}

      {/* MY DAY — doctor mode */}
      <Card className="p-5" testId="my-day-card">
        <p className="text-[11px] font-black tracking-[0.2em] uppercase text-[#D97B5A]">Doctor Mode</p>
        <p className="font-accent text-xl text-[#273127] mt-1 mb-3">What does your day look like?</p>
        <div className="flex flex-wrap gap-2">
          {DAY_TYPES.map((d) => (
            <button
              key={d}
              onClick={() => onMyDay(d)}
              data-testid={`myday-${d.toLowerCase().replace(/\s/g, '-')}`}
              className="px-4 py-2.5 rounded-full text-xs font-bold uppercase tracking-wide bg-[#FBF3EE] text-[#A85A3E] border border-[#F0DDD2] hover:bg-[#D97B5A] hover:text-white hover:border-[#D97B5A] transition-colors"
            >
              {d}
            </button>
          ))}
        </div>
      </Card>

      {/* 5 main cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {MAIN_CARDS.map((c, i) => (
          <Card key={c.id} className={`p-5 flex items-center gap-4 ${i === 4 ? 'sm:col-span-2 bg-[#2E4A3B] border-[#2E4A3B]' : ''}`} testId={`health-card-${c.id}`} onClick={() => go(c.id)}>
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-xl shrink-0 ${i === 4 ? 'bg-white/15' : 'bg-[#EDF2EB]'}`}>{c.icon}</div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-black tracking-wide ${i === 4 ? 'text-white' : 'text-[#273127]'}`}>{c.title}</p>
              <p className={`text-xs mt-0.5 ${i === 4 ? 'text-[#BFD6C4]' : 'text-[#66735F]'}`}>{c.sub}</p>
            </div>
            <span className={`text-lg ${i === 4 ? 'text-[#BFD6C4]' : 'text-[#9AA594]'}`}>→</span>
          </Card>
        ))}
      </div>

      {/* Footer message */}
      <div className="text-center py-8">
        <p className="font-accent text-2xl text-[#273127] leading-snug">YOU TAKE CARE OF EVERYONE.<br />NOW TAKE CARE OF YOU.</p>
        {!profile && (
          <PrimaryBtn onClick={() => go('assessment')} testId="footer-start-plan-btn" className="mt-5 px-10 py-4 text-base">START MY HEALTH PLAN</PrimaryBtn>
        )}
      </div>
    </div>
  );
}
